import { useState, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Typography, Avatar, Dropdown, Space, message } from 'antd'
import ResponsiveBreadcrumb from '../components/navigation/ResponsiveBreadcrumb'
import './AdminLayout.css'
import './AdminLayout-mobile-fix.css'
import {
  DashboardOutlined,
  FileTextOutlined,
  TeamOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyOutlined,
  BugOutlined,
  ThunderboltOutlined,
  MobileOutlined,
  EyeOutlined,
  MailOutlined,
  AudioOutlined,
  DesktopOutlined,
  BarChartOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import { useSessionManager } from '../hooks/useSessionManager'
import SessionTimeoutModal from '../components/common/SessionTimeoutModal'
import MinimalCookieConsent from '../components/common/MinimalCookieConsent'
import { useAuth } from '../contexts/useAuthContext'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '../components/common/LanguageSwitcher'

const { Header, Sider, Content } = Layout
const { Title } = Typography

const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, organization, signOut } = useAuth()
  const { t } = useTranslation()
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
      // Auto-collapse on mobile
      if (window.innerWidth < 768) {
        setCollapsed(true)
      }
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setCollapsed(true)
    }
  }, [location, isMobile])
  
  // Session management
  const { showTimeoutWarning, handleExtendSession, handleLogoutNow } = useSessionManager()
  
  const handleLogout = async () => {
    try {
      await signOut()
      message.success(t('auth.logoutSuccess'))
      navigate('/auth/login')
    } catch (error) {
      console.error('Logout error:', error)
      message.error(t('auth.logoutError'))
    }
  }

  // Menu items for sidebar
  const menuItems: MenuProps['items'] = [
    {
      key: '/admin',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/admin'),
    },
    {
      key: '/admin/reports',
      icon: <FileTextOutlined />,
      label: 'Reports',
      onClick: () => navigate('/admin/reports'),
    },
    {
      key: '/admin/users',
      icon: <TeamOutlined />,
      label: 'Users',
      onClick: () => navigate('/admin/users'),
    },
    {
      key: '/admin/analytics',
      icon: <BarChartOutlined />,
      label: 'Analytics',
      onClick: () => navigate('/admin/analytics'),
    },
    {
      key: '/admin/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/admin/settings'),
    },
    {
      key: 'testing',
      icon: <BugOutlined />,
      label: 'Testing Tools',
      children: [
        {
          key: 'functional-tests',
          label: 'Functional Tests',
          type: 'group',
          children: [
            {
              key: '/admin/test-suite',
              label: 'Test Suite',
              onClick: () => navigate('/admin/test-suite'),
            },
            {
              key: '/admin/test-auth',
              label: 'Auth Tests',
              onClick: () => navigate('/admin/test-auth'),
            },
          ],
        },
        {
          key: 'backend-tests',
          label: 'Backend & Security',
          type: 'group',
          children: [
            {
              key: '/admin/backend-check',
              icon: <SafetyOutlined />,
              label: 'Backend Check',
              onClick: () => navigate('/admin/backend-check'),
            },
            {
              key: '/admin/security-audit',
              icon: <SafetyOutlined />,
              label: 'Security Audit',
              onClick: () => navigate('/admin/security-audit'),
            },
            {
              key: '/admin/storage-test',
              icon: <SafetyOutlined />,
              label: 'Storage Test',
              onClick: () => navigate('/admin/storage-test'),
            },
            {
              key: '/admin/email-test',
              icon: <MailOutlined />,
              label: 'Email Test',
              onClick: () => navigate('/admin/email-test'),
            },
          ],
        },
        {
          key: 'performance-tests',
          label: 'Performance',
          type: 'group',
          children: [
            {
              key: '/admin/performance',
              icon: <ThunderboltOutlined />,
              label: 'Performance Monitor',
              onClick: () => navigate('/admin/performance'),
            },
            {
              key: '/admin/mobile-performance',
              icon: <MobileOutlined />,
              label: 'Mobile Performance',
              onClick: () => navigate('/admin/mobile-performance'),
            },
          ],
        },
        {
          key: 'ui-tests',
          label: 'UI/UX Tests',
          type: 'group',
          children: [
            {
              key: '/admin/responsive-test',
              label: 'Responsive Tests',
              onClick: () => navigate('/admin/responsive-test'),
            },
            {
              key: '/admin/responsive-dashboard',
              icon: <DesktopOutlined />,
              label: 'Responsive Dashboard',
              onClick: () => navigate('/admin/responsive-dashboard'),
            },
            {
              key: '/admin/accessibility',
              icon: <EyeOutlined />,
              label: 'Accessibility',
              onClick: () => navigate('/admin/accessibility'),
            },
            {
              key: '/admin/screen-reader',
              icon: <AudioOutlined />,
              label: 'Screen Reader',
              onClick: () => navigate('/admin/screen-reader'),
            },
          ],
        },
      ],
    },
  ]

  // User dropdown menu
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('common.profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('common.logout'),
      onClick: handleLogout,
    },
  ]

  return (
    <Layout className="min-h-screen">
      {/* Mobile overlay */}
      {isMobile && !collapsed && (
        <div 
          className="admin-mobile-overlay show"
          onClick={() => setCollapsed(true)}
        />
      )}
      
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="shadow-md"
        theme="light"
        width={isMobile ? 260 : 200}
        collapsedWidth={isMobile ? 0 : 80}
      >
        <div className="flex items-center justify-center h-16 border-b border-gray-200">
          <SafetyOutlined className="text-2xl text-blue-600" />
          {!collapsed && (
            <Title level={5} className="!mb-0 ml-2">
              Admin Panel
            </Title>
          )}
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultOpenKeys={['testing']}
          items={menuItems}
          className="border-r-0"
          style={{ height: 'calc(100% - 65px)', overflowY: 'auto', overflowX: 'hidden' }}
        />
      </Sider>

      <Layout>
        <Header className="bg-white px-4 shadow-sm flex items-center justify-between">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="text-gray-600 hover:text-gray-800 text-xl"
          >
            {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          </button>

          <Space>
            <span className="text-gray-600 admin-org-name">
              {organization?.name || 'WhistlePro'}
            </span>
            <LanguageSwitcher />
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space className="cursor-pointer">
                <Avatar icon={<UserOutlined />} className="bg-blue-500" />
                <span className="text-gray-700">
                  {user?.full_name || user?.email || t('common.user')}
                </span>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content className="m-6">
          <ResponsiveBreadcrumb />
          <div className="bg-white rounded-lg shadow-sm p-6 min-h-full responsive-p-3 mt-2">
            <Outlet />
          </div>
        </Content>
      </Layout>
      
      {/* Session Timeout Modal */}
      <SessionTimeoutModal
        visible={showTimeoutWarning}
        onExtend={handleExtendSession}
        onLogout={handleLogoutNow}
      />
      
      {/* Cookie Consent - Only shown on admin routes */}
      <MinimalCookieConsent />
    </Layout>
  )
}

export default AdminLayout